// The ZXing QR decoder for the phone Scan tab (barcode-detector's BarcodeDetector over
// zxing-wasm, zxing-cpp compiled to WebAssembly). It reads codes the default decoder (qr-scanner's
// jsQR worker, what iPhone Safari gets: Safari has no BarcodeDetector) misses: small, soft or
// low-contrast codes such as a pallet beacon's e-paper screen.
//
// The .wasm is served from our own site (scripts/copy-zxing-wasm.js explains the .txt name) and
// checked against the sha256 the package was built with before it is used. Any failure leaves
// the scanner on its default decoder: never a broken Scan tab.

const MAGIC = [0x00, 0x61, 0x73, 0x6d]; // "\0asm"

export function zxingWasmUrl(version, publicUrl = process.env.PUBLIC_URL || '') {
  return `${publicUrl}/zxing/zxing_reader-${version}.wasm.txt`;
}

export function looksLikeWasm(buf) {
  const b = new Uint8Array(buf, 0, Math.min(4, buf.byteLength));
  return b.length === 4 && MAGIC.every((v, i) => b[i] === v);
}

async function sha256Hex(buf) {
  if (!window.crypto || !window.crypto.subtle) return null; // http (not the real site): skip the check
  const d = await window.crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(d)).map((x) => x.toString(16).padStart(2, '0')).join('');
}

let ready = null;

// Resolves to the BarcodeDetector class (ponyfill), ready to decode; rejects on any failure.
export function loadZxing() {
  if (!ready) {
    ready = (async () => {
      const lib = await import('barcode-detector/ponyfill');
      const url = zxingWasmUrl(lib.ZXING_WASM_VERSION);
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`QR decoder not found (${res.status} ${url})`);
      const buf = await res.arrayBuffer();
      if (!looksLikeWasm(buf)) throw new Error(`QR decoder at ${url} is not WebAssembly`);
      const sum = await sha256Hex(buf);
      if (sum && lib.ZXING_WASM_SHA256 && sum !== lib.ZXING_WASM_SHA256) {
        throw new Error(`QR decoder at ${url} failed its checksum`);
      }
      await lib.prepareZXingModule({ overrides: { wasmBinary: buf }, fireImmediately: true });
      return lib.BarcodeDetector;
    })().catch((e) => {
      ready = null; // let a later scanner try again
      throw e;
    });
  }
  return ready;
}

// What the Scan tab reads: QR, and Data Matrix (surplus / owed bundle labels, 2026-10-26: the laser
// Zebra's EPL2 has no QR command, so those labels carry a Data Matrix of the same /s/ link).
export const SCAN_FORMATS = ['qr_code', 'data_matrix'];

// The detector class asking for SCAN_FORMATS whatever its caller asks for: qr-scanner always asks
// for ['qr_code'] and takes the first code found, whatever its format.
export function withScanFormats(Detector) {
  return class ScanFormatsDetector extends Detector {
    constructor(opts = {}) {
      super({ ...opts, formats: SCAN_FORMATS });
    }
  };
}

// Make qr-scanner use ZXing: it picks window.BarcodeDetector when there is one that reads QR.
// A browser with its own (Android Chrome) keeps it (QR only there). Returns true when ZXing is in use.
export async function installZxing() {
  if ('BarcodeDetector' in window) return false;
  const Detector = await loadZxing();
  window.BarcodeDetector = withScanFormats(Detector);
  return true;
}

// The camera to scan with on an iPhone: Safari lists the back cameras by name, and only the
// multi-lens ones ("Back Triple Camera", "Back Dual Wide Camera", "Back Dual Camera") switch to
// the close-focus lens on their own, as the Camera app does. The plain "Back Camera" can't focus
// on a code a few inches away. Anything else (Android's "camera2 0, facing back") = null: keep
// the default camera.
export function pickCloseFocusCamera(cameras) {
  const list = Array.isArray(cameras) ? cameras : [];
  for (const re of [/back.*triple/i, /back.*dual wide/i, /back.*dual/i]) {
    const c = list.find((x) => x && re.test(x.label || ''));
    if (c) return c;
  }
  return null;
}

// The whole frame, scaled down to at most `maxSide` on its long side (ZXing reads small codes
// from a bigger picture; the default region is the middle two-thirds at 400 x 400).
export function fullFrameRegion(width, height, maxSide = 1280) {
  const w = Math.max(1, width || 0);
  const h = Math.max(1, height || 0);
  const scale = Math.min(1, maxSide / Math.max(w, h));
  return {
    x: 0, y: 0, width: w, height: h,
    downScaledWidth: Math.round(w * scale), downScaledHeight: Math.round(h * scale),
  };
}

// Reads a QR code from a photo (the Take a photo button: the phone's own camera, full
// resolution, its own focus). Tries a 2000 px copy first (fast), then the full picture.
export async function decodePhoto(file, { Detector, fallback } = {}) {
  if (Detector) {
    const bitmap = await createImageBitmap(file);
    try {
      const det = new Detector({ formats: SCAN_FORMATS });
      for (const side of [2000, 0]) {
        let src = bitmap;
        const long = Math.max(bitmap.width, bitmap.height);
        if (side && long > side) {
          const s = side / long;
          const c = document.createElement('canvas');
          c.width = Math.round(bitmap.width * s);
          c.height = Math.round(bitmap.height * s);
          c.getContext('2d').drawImage(bitmap, 0, 0, c.width, c.height);
          src = c;
        } else if (side) {
          continue; // already small: the full-size pass reads it
        }
        const found = await det.detect(src);
        const hit = found.find((r) => r && r.rawValue);
        if (hit) return hit.rawValue;
      }
    } finally {
      if (bitmap.close) bitmap.close();
    }
  }
  if (fallback) {
    try {
      return await fallback(file);
    } catch (e) {
      return null; // qr-scanner throws "No QR code found"
    }
  }
  return null;
}
